/* Drawing cards and labels with pdf-lib, straight in the browser.

   The seven approved artwork pages are loaded as PDFs and embedded once per
   document, so a 2000-card file still comes out around 2 MB. Nothing is
   redrawn by hand: only the white panel is filled in. */

import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import QRCode from 'qrcode';
import * as L from './layout.js';

const cache = { templates: new Map(), fonts: null };

async function grab(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`could not load ${url} (${r.status})`);
  return new Uint8Array(await r.arrayBuffer());
}

async function templateBytes(slug) {
  if (!cache.templates.has(slug)) {
    cache.templates.set(slug, await grab(`assets/templates/${slug}.pdf`));
  }
  return cache.templates.get(slug);
}

/* Which characters the badge font can draw. Space Grotesk covers Latin in
   full -- accents, Vietnamese, the punctuation people paste -- but has no
   Chinese, Japanese or Korean and no emoji, and a character it lacks is drawn
   as an empty box: a guest's name printed with holes in it. Known only once the
   font has been read, which every render does before it draws anything. */
let canDraw = () => true;

async function fontBytes() {
  if (!cache.fonts) {
    const [bold, light] = await Promise.all([
      grab('assets/fonts/SpaceGrotesk-Bold.ttf'),
      grab('assets/fonts/SpaceGrotesk-Light.ttf'),
    ]);
    cache.fonts = { bold, light };
    // Values are set in the bold; the light carries only fixed English captions.
    const face = fontkit.create(bold);
    canDraw = cp => face.hasGlyphForCodePoint(cp);
  }
  return cache.fonts;
}

/** Warm the caches so the first scan at the desk isn't the slow one. */
export async function preload(slugs = L.PRINT_ORDER) {
  await fontBytes();
  await Promise.all(slugs.map(templateBytes));
}

/* One pixel per QR module. PDF images are drawn without interpolation by
   default, so the renderer scales these up as hard-edged squares — sharper in
   print than a resampled bitmap, a fraction of the size, and ~6x faster to
   encode, which is what makes a 3600-card run bearable in a browser tab. */
export async function qrPngDataUrl(payload) {
  return QRCode.toDataURL(payload, {
    errorCorrectionLevel: 'M', margin: 2, scale: 1,
    color: { dark: '#000000ff', light: '#ffffffff' },
  });
}

/* ------------------------------------------------------------------ text */

/* What the badge actually prints for a piece of text: whitespace collapsed,
   and anything the font cannot draw left off rather than printed as a box.
   Leaving characters off can strand their surroundings -- "Steven Chen (陈志强)"
   would become "Steven Chen ()" -- so brackets left empty and separators left
   dangling go too. The roster and every export keep the text as given; only
   the ink on the card changes. */
const EMPTY_BRACKETS = /[(\[{（【「『]\s*[)\]}）】」』]/g;
const EDGE_JUNK = /^[\s,;:/|·•\-–—]+|[\s,;:/|·•\-–—]+$/g;

const clean = s => [...String(s ?? '').replace(/\s+/g, ' ')]   // by code point
  .filter(ch => canDraw(ch.codePointAt(0)))
  .join('')
  .replace(EMPTY_BRACKETS, ' ')
  .replace(/\s+/g, ' ')
  .replace(EDGE_JUNK, '')
  .trim();

/** The characters in a piece of text that the badge cannot print. */
export function undrawable(s) {
  const out = new Set();
  for (const ch of String(s ?? '')) {
    if (/\s/.test(ch)) continue;
    if (!canDraw(ch.codePointAt(0))) out.add(ch);
  }
  return [...out];
}

/** The text as it will appear on the badge. */
export const asPrinted = s => clean(s);

function fitSize(text, slot, font) {
  let size = slot.size;
  while (size > slot.minSize) {
    if (font.widthOfTextAtSize(text, size) <= slot.maxWidth) return size;
    size -= 0.25;
  }
  return slot.minSize;
}

/* Prefer the most even split when both halves fit — that is what the artwork
   does with a two-line pillar. When the text is simply too long, fall back to
   filling the first line greedily so only the tail is cut, instead of losing
   the ends of both lines. */
function wrapTwo(text, font, size, maxWidth) {
  const words = text.split(' ');
  if (words.length < 2) return [text];

  let balanced = null;
  let greedy = null;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' ');
    const b = words.slice(i).join(' ');
    const wa = font.widthOfTextAtSize(a, size);
    const wb = font.widthOfTextAtSize(b, size);
    const widest = Math.max(wa, wb);
    if (!balanced || widest < balanced.widest) balanced = { widest, lines: [a, b] };
    if (wa <= maxWidth) greedy = [a, b];
  }
  if (balanced && balanced.widest <= maxWidth) return balanced.lines;
  return greedy || balanced?.lines || [text];
}

function ellipsise(text, slot, font, size) {
  if (font.widthOfTextAtSize(text, size) <= slot.maxWidth) return text;
  let t = text;
  while (t && font.widthOfTextAtSize(t + '…', size) > slot.maxWidth) {
    t = t.slice(0, -1);
  }
  return t ? t.trimEnd() + '…' : '';
}

/** Draw one slot, shrinking then — where allowed — wrapping to fit. */
function drawSlot(page, text, slot, fonts) {
  const value = clean(text);
  if (!value) return 0;
  const font = slot.weight === 'light' ? fonts.light : fonts.bold;
  const ink = slot.grey === undefined ? rgb(0, 0, 0)
    : rgb(slot.grey, slot.grey, slot.grey);
  let size = fitSize(value, slot, font);

  if (font.widthOfTextAtSize(value, size) <= slot.maxWidth || slot.maxLines === 1) {
    page.drawText(ellipsise(value, slot, font, size), {
      x: slot.x, y: slot.baseline, size, font, color: ink,
    });
    return 1;
  }

  size = slot.wrap === 'down' ? slot.size : slot.size * 0.8;
  let lines = wrapTwo(value, font, size, slot.maxWidth);
  while (size > slot.minSize &&
         Math.max(...lines.map(l => font.widthOfTextAtSize(l, size))) > slot.maxWidth) {
    size -= 0.25;
    lines = wrapTwo(value, font, size, slot.maxWidth);
  }
  const leading = size * slot.leading;
  lines.forEach((line, i) => {
    const step = slot.wrap === 'up' ? lines.length - 1 - i : -i;
    page.drawText(ellipsise(line, slot, font, size), {
      x: slot.x, y: slot.baseline + leading * step, size, font, color: ink,
    });
  });
  return lines.length;
}

/* What actually goes under the name, in order, with the duplicates real data
   is full of removed: exports often repeat the company as the pillar, and a
   position that just restates the company adds nothing to a badge. */
export function detailLines(a, fields) {
  const out = [];
  for (const key of fields) {
    const value = clean(a[key]);
    if (!value) continue;
    // A heading with nothing under it is worse than no heading, and a company
    // repeated as its own pillar reads as a mistake.
    if (out.some(e => e.value.toLowerCase() === value.toLowerCase())) continue;
    const field = L.DETAIL_FIELDS.find(f => f.key === key);
    out.push({ value, label: field ? field.card : '' });
  }
  return out;
}

/* Stack heading-and-value pairs downwards from the company baseline.
 *
 * Three labelled fields fit the panel exactly; a value that wraps to a second
 * line does not. Rather than silently dropping whichever field came last — on a
 * badge, a missing job title is a defect, not a tidy-up — the block measures
 * itself first and shrinks until it fits. */
function detailMetrics(entries, fonts, scale) {
  const D = L.DETAILS;
  const size = D.size * scale;
  const leading = size * D.leading;
  let height = 0;
  const shaped = entries.map(entry => {
    const right = D.rightBesideQr;              // measure at the narrow width
    const maxWidth = right - D.x;
    const lines = font_lines(entry.value, fonts.light, size, maxWidth);
    height += D.valueDrop * scale + (lines - 1) * leading + D.pairGap * scale;
    return { ...entry, lines };
  });
  return { shaped, height, size, leading };
}

/* How many lines this value needs at that size — one, or two after wrapping. */
function font_lines(text, font, size, maxWidth) {
  if (font.widthOfTextAtSize(text, size) <= maxWidth) return 1;
  return 2;
}

function flowDetails(page, entries, fonts, { ox = 0, oy = 0, scale = 1 } = {}) {
  const D = L.DETAILS;
  if (!entries.length) return;

  const available = D.firstBaseline - D.bottom;
  let shrink = 1;
  let m = detailMetrics(entries, fonts, shrink);
  while (m.height > available && shrink > 0.66) {
    shrink -= 0.04;
    m = detailMetrics(entries, fonts, shrink);
  }

  const size = Math.max(D.minSize, D.size * shrink);
  const labelSize = Math.max(D.labelMinSize, D.labelSize * shrink);
  let baseline = D.firstBaseline;

  for (const entry of m.shaped) {
    if (baseline - D.valueDrop * shrink < D.bottom) break;   // genuinely no room

    if (entry.label) {
      drawSlot(page, entry.label, scaled({
        x: D.x, baseline, size: labelSize, minSize: D.labelMinSize,
        maxWidth: D.rightFull - D.x, maxLines: 1, wrap: 'up',
        leading: 1, weight: 'light', grey: D.labelGrey,
      }, ox, oy, scale), fonts);
    }

    const valueBaseline = baseline - D.valueDrop * shrink;
    // Full measure while the line clears the QR; narrow once it sits beside it.
    const right = valueBaseline - size * 0.25 > D.qrTop
      ? D.rightFull : D.rightBesideQr;
    const used = drawSlot(page, entry.value, scaled({
      x: D.x, baseline: valueBaseline, size, minSize: D.minSize,
      maxWidth: right - D.x, maxLines: 2, wrap: 'down',
      leading: D.leading, weight: 'bold',
    }, ox, oy, scale), fonts);

    baseline = valueBaseline - (used - 1) * size * D.leading - D.pairGap * shrink;
  }
}

/* Map a slot from card coordinates onto wherever it is being drawn. */
function scaled(slot, ox, oy, scale) {
  if (scale === 1 && !ox && !oy) return slot;
  return {
    ...slot,
    x: ox + slot.x * scale,
    baseline: oy + slot.baseline * scale,
    size: slot.size * scale,
    minSize: slot.minSize * scale,
    maxWidth: slot.maxWidth * scale,
  };
}

/* ----------------------------------------------------------------- cards */

const SHEETS = { a4: [210 * L.MM, 297 * L.MM], a3: [297 * L.MM, 420 * L.MM] };

/* The card stock is smaller than the artboard it was designed on. The whole
   card -- artwork, text, QR -- is scaled by one factor so nothing moves
   relative to anything else; only the height is given, and the width follows
   from the artboard's own proportions (95 x 130). At 109 mm that is 79.65 mm
   across, which is the "about 8 cm" measured off the printed cards. */
export const DEFAULT_CARD_HEIGHT_MM = 109;

export const cardScale = (heightMm = DEFAULT_CARD_HEIGHT_MM) =>
  (Number(heightMm) * L.MM) / L.CARD_H;

export const cardSizeMm = (heightMm = DEFAULT_CARD_HEIGHT_MM) => ({
  widthMm: (L.CARD_W / L.MM) * cardScale(heightMm),
  heightMm: Number(heightMm),
});

export function sheetLayout(mode, scale = 1, gap = 4 * L.MM) {
  const cw = L.CARD_W * scale;
  const ch = L.CARD_H * scale;
  if (mode === 'single') {
    return { size: [cw, ch], slots: [[0, 0]], cw, ch };
  }
  const [w, h] = SHEETS[mode];
  const cols = Math.max(1, Math.floor((w + gap) / (cw + gap)));
  const rows = Math.max(1, Math.floor((h + gap) / (ch + gap)));
  const blockW = cols * cw + (cols - 1) * gap;
  const blockH = rows * ch + (rows - 1) * gap;
  const left = (w - blockW) / 2;
  const bottom = (h - blockH) / 2;
  const slots = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      slots.push([left + c * (cw + gap), bottom + (rows - 1 - r) * (ch + gap)]);
    }
  }
  return { size: [w, h], slots, cw, ch };
}

function cropMarks(page, ox, oy, cw, ch, len = 3 * L.MM, off = 1 * L.MM) {
  const line = (x1, y1, x2, y2) => page.drawLine({
    start: { x: x1, y: y1 }, end: { x: x2, y: y2 },
    thickness: 0.25, color: rgb(0, 0, 0),
  });
  for (const [cx, sx] of [[ox, -1], [ox + cw, 1]]) {
    for (const [cy, sy] of [[oy, -1], [oy + ch, 1]]) {
      line(cx + sx * off, cy, cx + sx * (off + len), cy);
      line(cx, cy + sy * off, cx, cy + sy * (off + len));
    }
  }
}

/**
 * Render a run of cards into one PDF.
 * @returns {Promise<Uint8Array>}
 */
export async function renderCards(attendees, {
  pageMode = 'single', showCropMarks = true, fields = L.DEFAULT_FIELDS,
  cardHeightMm = DEFAULT_CARD_HEIGHT_MM, onProgress,
} = {}) {
  const s = cardScale(cardHeightMm);
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  doc.setTitle('SMDD 2026 ID cards');

  const fb = await fontBytes();
  const fonts = {
    bold: await doc.embedFont(fb.bold, { subset: true }),
    light: await doc.embedFont(fb.light, { subset: true }),
  };

  const embedded = new Map();
  const forSlug = async slug => {
    if (!embedded.has(slug)) {
      const [pg] = await doc.embedPdf(await templateBytes(slug));
      embedded.set(slug, pg);
    }
    return embedded.get(slug);
  };

  const { size, slots, cw, ch } = sheetLayout(pageMode, s);
  let page = null;

  for (let i = 0; i < attendees.length; i++) {
    const a = attendees[i];
    const cat = L.BY_SLUG[a.category];
    if (!cat) continue;
    const idx = i % slots.length;
    if (idx === 0) page = doc.addPage(size);
    const [ox, oy] = slots[idx];

    page.drawPage(await forSlug(a.category), { x: ox, y: oy, xScale: s, yScale: s });
    const at = slot => scaled(slot, ox, oy, s);
    drawSlot(page, a.name, at(L.NAME), fonts);
    flowDetails(page, detailLines(a, fields), fonts, { ox, oy, scale: s });
    drawSlot(page, cat.label, at(L.categorySlot(cat)), fonts);
    if (a.qrPng) {
      const img = await doc.embedPng(a.qrPng);
      const side = Math.min(L.QR_BOX.w, L.QR_BOX.h);
      page.drawImage(img, {
        x: ox + (L.QR_BOX.x + (L.QR_BOX.w - side) / 2) * s,
        y: oy + (L.QR_BOX.y + (L.QR_BOX.h - side) / 2) * s,
        width: side * s, height: side * s,
      });
    }
    if (pageMode !== 'single' && showCropMarks) cropMarks(page, ox, oy, cw, ch);
    if (onProgress && (i % 25 === 0 || i === attendees.length - 1)) {
      onProgress(i + 1, attendees.length);
      await new Promise(r => setTimeout(r, 0));   // let the UI breathe
    }
  }
  return doc.save();
}

/* ---------------------------------------------------------------- blanks */

/**
 * The artwork with its white panel left empty, one page per category, at the
 * size the cards are printed -- for running cards off ahead of the event and
 * putting a label on at the desk. Drawn with the same scale as a filled card,
 * so a blank and a printed card come off the same stock identically.
 */
export async function renderBlanks(slugs, { cardHeightMm = DEFAULT_CARD_HEIGHT_MM } = {}) {
  const doc = await PDFDocument.create();
  // The title a PDF viewer shows, so a file opened from Downloads says which
  // category it is without reading the file name.
  const one = slugs.length === 1 && L.BY_SLUG[slugs[0]];
  doc.setTitle(one ? `SMDD 2026 blank card - ${one.label}` : 'SMDD 2026 blank cards');
  const s = cardScale(cardHeightMm);
  const { size } = sheetLayout('single', s);
  for (const slug of slugs) {
    if (!L.BY_SLUG[slug]) continue;
    const [tpl] = await doc.embedPdf(await templateBytes(slug));
    doc.addPage(size).drawPage(tpl, { x: 0, y: 0, xScale: s, yScale: s });
  }
  return doc.save();
}

/* ---------------------------------------------------------------- labels */

/** How the card's white panel is fitted onto a sheet of label stock. */
export function labelFit({ widthMm = 80, heightMm = 55, marginMm = 0 } = {}) {
  const usableW = (widthMm - 2 * marginMm) * L.MM;
  const usableH = (heightMm - 2 * marginMm) * L.MM;
  const scale = Math.min(usableW / L.CARD_W, usableH / L.PANEL_H);
  return {
    scale,
    dx: (widthMm * L.MM - L.CARD_W * scale) / 2,
    dy: (heightMm * L.MM - L.PANEL_H * scale) / 2,
    size: [widthMm * L.MM, heightMm * L.MM],
  };
}

/**
 * Labels for the registration desk: exactly what a blank card is missing, so
 * the sticker drops straight into the white panel. One page per attendee.
 */
export async function renderLabels(attendees, {
  widthMm = 80, heightMm = 55, marginMm = 0, includeQr = true, guides = false,
  fields = L.DEFAULT_FIELDS, onProgress,
} = {}) {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  const fb = await fontBytes();
  const fonts = {
    bold: await doc.embedFont(fb.bold, { subset: true }),
    light: await doc.embedFont(fb.light, { subset: true }),
  };
  const fit = labelFit({ widthMm, heightMm, marginMm });

  for (let i = 0; i < attendees.length; i++) {
    const a = attendees[i];
    const cat = L.BY_SLUG[a.category];
    if (!cat) continue;
    const page = doc.addPage(fit.size);
    if (guides) {
      page.drawRectangle({
        x: 0.5, y: 0.5, width: fit.size[0] - 1, height: fit.size[1] - 1,
        borderColor: rgb(0.8, 0.8, 0.8), borderWidth: 0.3,
      });
    }
    // Map card coordinates onto the label: scale about the panel's origin.
    const oy = fit.dy - L.PANEL_Y * fit.scale;
    const put = slot => scaled(slot, fit.dx, oy, fit.scale);
    drawSlot(page, a.name, put(L.NAME), fonts);
    flowDetails(page, detailLines(a, fields), fonts,
                { ox: fit.dx, oy, scale: fit.scale });
    drawSlot(page, cat.label, put(L.categorySlot(cat)), fonts);

    if (includeQr && a.qrPng) {
      const img = await doc.embedPng(a.qrPng);
      const box = Math.min(L.QR_BOX.w, L.QR_BOX.h);
      page.drawImage(img, {
        x: fit.dx + (L.QR_BOX.x + (L.QR_BOX.w - box) / 2) * fit.scale,
        y: fit.dy + (L.QR_BOX.y - L.PANEL_Y) * fit.scale,
        width: box * fit.scale, height: box * fit.scale,
      });
    }
    if (onProgress && (i % 25 === 0 || i === attendees.length - 1)) {
      onProgress(i + 1, attendees.length);
      await new Promise(r => setTimeout(r, 0));
    }
  }
  return doc.save();
}

/** Attach a rendered QR bitmap to each attendee, in place. */
export async function attachQrCodes(attendees, onProgress) {
  for (let i = 0; i < attendees.length; i++) {
    const a = attendees[i];
    if (!a.qrPng && a.qr) a.qrPng = await qrPngDataUrl(a.qr);
    if (onProgress && (i % 50 === 0 || i === attendees.length - 1)) {
      onProgress(i + 1, attendees.length);
      await new Promise(r => setTimeout(r, 0));
    }
  }
  return attendees;
}
